import { useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useAppContext } from '../components/Layout';
import Spinner from '../components/Spinner';
import Modal from '../components/Modal';
import {
  useCommunicationPreview,
  useCommunicationsAuthorization,
  useVisibleCommunications,
  type CommunicationsAuthorization,
} from '../hooks/useCommunications';
import { functions } from '../firebase';
import { visibleDistricts } from '../lib/visibility';
import { useOutboundEmailMode } from '../hooks/useOutboundEmailMode';
import {
  COMM_SCOPE_LABELS,
  COMMS_ALL_COUNCIL,
  COMMS_FROM_ADDRESS,
  type CommScope,
  type Communication,
  type CommunicationTypeId,
  type UserProfile,
} from '../types';

type Toast = { id: number; kind: 'ok' | 'warn'; text: string };

interface ComposeAttachment {
  name: string;
  size: number;
  contentType: string;
  content: string; // base64
}

// Mirrors the server's limits (functions/src/communications.ts), which are
// the real enforcement — these only give an immediate message in the browser.
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 6_000_000;
const BLOCKED_EXTENSIONS = new Set([
  'ade', 'adp', 'app', 'bat', 'bin', 'cab', 'chm', 'cmd', 'com', 'cpl', 'crt', 'dll', 'exe', 'hlp', 'hta', 'inf',
  'ins', 'isp', 'jar', 'js', 'jse', 'lnk', 'mdb', 'mde', 'msc', 'msi', 'msp', 'mst', 'pcd', 'pif', 'reg', 'scr',
  'sct', 'sh', 'shs', 'vb', 'vba', 'vbe', 'vbs', 'vxd', 'wsc', 'wsf', 'wsh',
]);

function formatBytes(bytes: number): string {
  return bytes >= 1_000_000 ? `${(bytes / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1000))} KB`;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file.'));
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.readAsDataURL(file);
  });
}

export default function Communications() {
  const { profile } = useAppContext();
  const authorization = useCommunicationsAuthorization(profile);
  const canSend = authorization.data?.canSend === true;
  const [tab, setTab] = useState<'compose' | 'history'>('history');
  const initialized = useRef(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    if (!authorization.loading && !initialized.current) {
      initialized.current = true;
      setTab(canSend ? 'compose' : 'history');
    }
  }, [authorization.loading, canSend]);
  useEffect(() => {
    if (!authorization.loading && !canSend && tab === 'compose') setTab('history');
  }, [authorization.loading, canSend, tab]);

  const notify = (kind: Toast['kind'], text: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 6000);
  };

  return (
    <>
      <section className="iv-intro">
        <div>
          <h2>Communications</h2>
          <p>Email district and council leadership without hand-picking addresses.</p>
        </div>
      </section>

      <div className="uh-viewtabs">
        {canSend && (
          <button className={tab === 'compose' ? 'active' : ''} onClick={() => setTab('compose')}>
            Compose
          </button>
        )}
        <button className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>
          History
        </button>
      </div>

      {authorization.loading && <div className="empty"><Spinner label="Checking Communications access…" /></div>}
      {authorization.error && (
        <div className="card empty">Communications sending is unavailable because current policy authorization could not be verified.</div>
      )}
      {tab === 'compose' && canSend && authorization.data && (
        <Compose
          profile={profile}
          authorization={authorization.data}
          notify={notify}
          onSent={() => setTab('history')}
        />
      )}

      {tab === 'history' && <History profile={profile} />}

      {toasts.length > 0 && (
        <div className="iv-toasts">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`iv-toast s-${t.kind === 'ok' ? 'ok' : 'warn'}`}
              onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))}
            >
              {t.text}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ---- compose --------------------------------------------------------

function Compose({
  profile,
  authorization,
  notify,
  onSent,
}: {
  profile: UserProfile;
  authorization: CommunicationsAuthorization;
  notify: (kind: Toast['kind'], text: string) => void;
  onSent: () => void;
}) {
  const { settings } = useAppContext();
  const districtOptions = useMemo(
    () => authorization.tenantWide
      ? settings.districts
      : visibleDistricts(profile, settings.districts).filter((district) => authorization.districtIds.includes(district.id)),
    [authorization.districtIds, authorization.tenantWide, profile, settings.districts],
  );

  const [districtId, setDistrictId] = useState(
    authorization.tenantWide ? COMMS_ALL_COUNCIL : districtOptions[0]?.id ?? '',
  );
  const [scope, setScope] = useState<CommScope>('districtCommittee');
  const [communicationTypeId, setCommunicationTypeId] = useState<CommunicationTypeId | ''>(
    authorization.communicationTypes[0]?.id ?? '',
  );
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const sendAttempt = useRef<{ fingerprint: string; requestId: string } | null>(null);
  // Display only — the server decides. Anything other than an explicit live council is a demonstration: nothing is delivered.
  const outboundMode = useOutboundEmailMode();
  const previewMode = outboundMode === 'preview';
  const [attachments, setAttachments] = useState<ComposeAttachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const attachedBytes = attachments.reduce((sum, a) => sum + a.size, 0);

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setAttachError(null);
    const next = [...attachments];
    for (const file of Array.from(files)) {
      const extension = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
      if (BLOCKED_EXTENSIONS.has(extension)) {
        setAttachError(`“${file.name}” is a file type that can’t be sent by email.`);
        continue;
      }
      if (next.some((a) => a.name === file.name)) {
        setAttachError(`“${file.name}” is already attached.`);
        continue;
      }
      if (next.length >= MAX_ATTACHMENTS) {
        setAttachError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
        break;
      }
      if (next.reduce((sum, a) => sum + a.size, 0) + file.size > MAX_ATTACHMENT_BYTES) {
        setAttachError(`Attachments can total ${MAX_ATTACHMENT_BYTES / 1_000_000} MB at most — “${file.name}” would go over.`);
        continue;
      }
      const content = await readAsBase64(file);
      next.push({ name: file.name, size: file.size, contentType: file.type || 'application/octet-stream', content });
    }
    setAttachments(next);
    if (fileInput.current) fileInput.current.value = '';
  };

  const isAll = districtId === COMMS_ALL_COUNCIL;
  const preview = useCommunicationPreview(districtId, scope, communicationTypeId, true);
  const resolved = preview.data;
  const selectedType = authorization.communicationTypes.find((item) => item.id === communicationTypeId);

  const ready = !!communicationTypeId && !!subject.trim() && !!body.trim();
  const canFire = ready && !!resolved && resolved.count > 0 && !busy && !preview.loading;

  const send = async (test: boolean) => {
    setBusy(true);
    try {
      const fingerprint = JSON.stringify({ districtId, scope, communicationTypeId, subject, body, files: attachments.map((a) => `${a.name}:${a.size}`) });
      if (!test && sendAttempt.current?.fingerprint !== fingerprint) {
        sendAttempt.current = { fingerprint, requestId: crypto.randomUUID() };
      }
      const call = httpsCallable<
        Record<string, unknown>,
        { recipientCount: number; simulated?: boolean; previewOnly?: boolean }
      >(functions, 'sendCommunication');
      const res = await call({
        districtId,
        scope,
        communicationTypeId,
        subject,
        body,
        test,
        attachments: attachments.map(({ name, contentType, content }) => ({ name, contentType, content })),
        ...(!test ? { requestId: sendAttempt.current!.requestId } : {}),
      });
      const sim = res.data.simulated ? ' (simulated — email delivery not live yet)' : '';
      if (res.data.previewOnly) {
        // Server-enforced preview: nothing was transmitted to anyone, including the sender.
        if (test) {
          notify('ok', 'Test previewed — nothing was sent. This is a demonstration council; messages are not delivered.');
        } else {
          const n = res.data.recipientCount;
          notify('ok', `Previewed for ${n} recipient${n === 1 ? '' : 's'} — nothing was delivered. This is a demonstration council.`);
        }
      } else if (test) {
        notify('ok', `Test sent to ${profile.email}${sim}.`);
      } else {
        const n = res.data.recipientCount;
        notify('ok', `Sent to ${n} recipient${n === 1 ? '' : 's'}${sim}.`);
      }
      if (!test) {
        setSubject('');
        setBody('');
        setAttachments([]);
        sendAttempt.current = null;
        onSent();
      }
    } catch (e) {
      notify('warn', e instanceof Error ? e.message : 'Send failed.');
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  const visibilityNote = isAll
    ? 'Every leader can see that this was sent and by whom; only recipients and admins can read the message.'
    : 'Any leader in this district can see that this was sent and by whom; only recipients and admins can read the message.';

  return (
    <div className="card comm-form">
      {previewMode && (
        <div className="notice" role="status" style={{ marginBottom: 12, padding: '10px 14px', border: '1px solid var(--line)', borderRadius: 8, background: 'var(--blue-soft, #eaf3fb)' }}>
          <b>Demonstration council — messages are previewed, not delivered.</b>{' '}
          Choose audiences, see the recipient count, compose and review exactly who would receive a message. Pressing Send
          records a preview only; no email is transmitted to anyone, including you.
        </div>
      )}
      <label>From</label>
      <p className="subtitle" style={{ marginTop: 0 }}>
        <b>{profile.displayName ? `${profile.displayName} via Compass` : 'Compass'}</b>
        {' '} &lt;{COMMS_FROM_ADDRESS}&gt; · replies always go directly to <b>{profile.email}</b>
      </p>

      <label>Communication Type</label>
      <select
        value={communicationTypeId}
        onChange={(e) => setCommunicationTypeId(e.target.value as CommunicationTypeId)}
      >
        {authorization.communicationTypes.map((item) => (
          <option key={item.id} value={item.id}>{item.label}</option>
        ))}
      </select>
      {selectedType && <p className="subtitle" style={{ marginTop: -4 }}>{selectedType.description}</p>}

      <div className="form-row">
        <div>
          <label>District</label>
          <select value={districtId} onChange={(e) => setDistrictId(e.target.value)}>
            {authorization.tenantWide && <option value={COMMS_ALL_COUNCIL}>All Council</option>}
            {districtOptions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Scope</label>
          <select value={scope} onChange={(e) => setScope(e.target.value as CommScope)}>
            {(Object.keys(COMM_SCOPE_LABELS) as CommScope[]).map((s) => (
              <option key={s} value={s}>
                {COMM_SCOPE_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <label>Subject</label>
      <input value={subject} onChange={(e) => setSubject(e.target.value)} />

      <label>Message</label>
      <textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} />

      <label>Attachments</label>
      <div>
        <input
          ref={fileInput}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => void addFiles(e.target.files)}
        />
        <button type="button" className="btn" disabled={busy || attachments.length >= MAX_ATTACHMENTS} onClick={() => fileInput.current?.click()}>
          + Attach files
        </button>
        <span className="subtitle" style={{ marginLeft: 10 }}>
          Up to {MAX_ATTACHMENTS} files, {MAX_ATTACHMENT_BYTES / 1_000_000} MB total. Every recipient receives them.
        </span>
        {attachError && <div className="err" style={{ marginTop: 6 }}>{attachError}</div>}
        {attachments.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {attachments.map((a) => (
              <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                <span>{a.name}</span>
                <span className="subtitle">{formatBytes(a.size)}</span>
                <button
                  type="button"
                  className="btn"
                  aria-label={`Remove ${a.name}`}
                  disabled={busy}
                  onClick={() => setAttachments(attachments.filter((x) => x.name !== a.name))}
                >
                  Remove
                </button>
              </div>
            ))}
            <div className="subtitle">{formatBytes(attachedBytes)} attached</div>
          </div>
        )}
      </div>

      <div className="comm-preview">
        {preview.loading && <Spinner label="Resolving authorized recipients…" />}
        {preview.error && (
          <div className="subtitle">Recipient preview is unavailable. Sending remains disabled until it can be authorized.</div>
        )}
        {resolved && (
          <>
            <div className="comm-preview-head">
              <span className="comm-count">{resolved.count}</span>
              <span>
                <b>{resolved.audienceLabel}</b>
                <div className="subtitle">{resolved.description}</div>
              </span>
            </div>

            {resolved.count === 0 ? (
              <div className="subtitle">No recipients for this selection.</div>
            ) : (
              resolved.groups.map((g) => (
                <div key={g.label} className="comm-group">
                  <div className="comm-group-head">
                    {g.label} · {g.people.length}
                  </div>
                  {g.people.slice(0, 25).map((p, i) => (
                    <div key={i} className="comm-person">
                      <span>{p.name}</span>
                      <span className="subtitle">{p.role}</span>
                    </div>
                  ))}
                  {g.people.length > 25 && (
                    <div className="subtitle" style={{ padding: '3px 0' }}>
                      …and {g.people.length - 25} more
                    </div>
                  )}
                </div>
              ))
            )}

            <div className="comm-foot subtitle">
              From {profile.displayName ? `${profile.displayName} via Compass` : 'Compass'} &lt;{COMMS_FROM_ADDRESS}&gt;
              {' '}· replies go to <b>{profile.email}</b>. {visibilityNote}
            </div>
            <div className="comm-foot subtitle" style={{ marginTop: 6 }}>
              <b>Private delivery.</b> Compass sends each recipient their own individually addressed
              message, never one shared email. No recipient can see who else received it, and the list
              above is visible only to you. Replies come back to you alone.
            </div>
          </>
        )}
      </div>

      <div className="actions" style={{ display: 'flex', gap: 24, marginTop: 14 }}>
        <button className="btn primary" disabled={!canFire} onClick={() => setConfirm(true)}>
          {previewMode ? 'Preview send to' : 'Send to'} {resolved?.count ?? 0} recipient{resolved?.count === 1 ? '' : 's'}
        </button>
        <button
          className="btn"
          style={{ background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' }}
          disabled={!ready || busy}
          onClick={() => void send(true)}
        >
          {previewMode ? 'Preview test to myself' : 'Send test to myself'}
        </button>
      </div>

      {confirm && (
        <Modal title={previewMode ? 'Preview this message?' : 'Send this message?'} onClose={() => setConfirm(false)}>
          <p>
            {previewMode ? 'This previews ' : 'This sends '}<b>“{subject}”</b> to {resolved?.count ?? 0} recipient
            {resolved?.count === 1 ? '' : 's'} ({resolved?.audienceLabel}) as a{' '}
            <b>{resolved?.communicationType.label ?? selectedType?.label}</b>.{' '}
            {previewMode ? 'This is a demonstration council: it is recorded in the history as previewed and nothing is delivered.' : 'This can’t be undone.'}
          </p>
          <div className="actions">
            <button className="btn" onClick={() => setConfirm(false)}>
              Cancel
            </button>
            <button className="btn primary" disabled={busy} onClick={() => void send(false)}>
              {busy ? (previewMode ? 'Previewing…' : 'Sending…') : `${previewMode ? 'Preview for' : 'Send to'} ${resolved?.count ?? 0}`}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---- history -------------------------------------------------------

function History({ profile }: { profile: UserProfile }) {
  const q = useVisibleCommunications(profile);
  const [view, setView] = useState<Communication | null>(null);

  if (q.loading) return <div className="empty"><Spinner label="Loading…" /></div>;
  if (q.data.length === 0) return <div className="card empty">No messages sent yet.</div>;

  return (
    <>
      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="uh-table">
          <thead>
            <tr>
              <th>Sent</th>
              <th>By</th>
              <th>Audience</th>
              <th>Type</th>
              <th>Subject</th>
              <th>Recipients</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {q.data.map((c) => (
              <tr key={c.id}>
                <td>
                  {c.sentAt
                    ? new Date(c.sentAt.toMillis()).toLocaleString(undefined, {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })
                    : '—'}
                </td>
                <td>{c.sentByName}</td>
                <td>{c.audienceLabel}</td>
                <td>{c.communicationTypeLabel ?? 'Legacy / Unclassified'}</td>
                <td>{c.subject}</td>
                <td>{c.recipientCount}</td>
                <td>
                  {c.previewOnly ? (
                    <b title="This communication was previewed in a demonstration council. It was not delivered to anyone.">Previewed — not delivered</b>
                  ) : c.status.accepted != null ? `${c.status.accepted} accepted` : `${c.status.sent ?? 0} sent`}
                  {!c.previewOnly && c.status.suppressed ? ` · ${c.status.suppressed} suppressed` : ''}
                  {c.status.bounced ? ` · ${c.status.bounced} bounced` : ''}
                  {c.status.otherRejected ? ` · ${c.status.otherRejected} rejected` : ''}
                  {c.status.uncertainRecipients ? ` · ${c.status.uncertainRecipients} need review` : ''}
                  {c.deliveryState === 'sending' ? ' · sending' : ''}
                </td>
                <td>
                  <button className="btn" onClick={() => setView(c)}>
                    View message
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {view && <ViewMessage comm={view} onClose={() => setView(null)} />}
    </>
  );
}

function ViewMessage({ comm, onClose }: { comm: Communication; onClose: () => void }) {
  const [state, setState] = useState<{ loading: boolean; body?: string; denied?: boolean }>({
    loading: true,
  });

  useEffect(() => {
    let live = true;
    const call = httpsCallable<{ communicationId: string }, { body: string }>(functions, 'getCommunicationBodyV2');
    void call({ communicationId: comm.id })
      .then((result) => {
        if (!live) return;
        setState({ loading: false, body: result.data.body });
      })
      .catch(() => live && setState({ loading: false, denied: true }));
    return () => {
      live = false;
    };
  }, [comm.id]);

  return (
    <Modal title={comm.subject} onClose={onClose}>
      <div className="subtitle" style={{ marginBottom: 10 }}>
        {comm.previewOnly ? <b>Previewed — not delivered · </b> : null}
        {comm.communicationTypeLabel ?? 'Legacy / Unclassified'} · {comm.audienceLabel} · {comm.recipientCount} recipient
        {comm.recipientCount === 1 ? '' : 's'} {comm.previewOnly ? 'would have received it' : '· sent by ' + comm.sentByName}
        {' · replies to '}
        {comm.replyTo}
      </div>
      {state.loading && <Spinner label="Loading…" />}
      {state.denied && (
        <p className="subtitle">
          This message was sent to {comm.recipientCount} leaders. Only those recipients and
          Compass admins can read the contents.
        </p>
      )}
      {!state.loading && !state.denied && <pre className="comm-body">{state.body}</pre>}
    </Modal>
  );
}
